import { FC, PropsWithChildren, useCallback } from 'react';
import classnames from 'classnames';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import responseSelectButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/response_select_button_click';
import { Card, useBreakpoint, useLoadingState } from '@hh.ru/magritte-ui';

import useVacancyId from 'src/components/ResumeCard/hooks/useVacancyId';
import useSearchResultShown from 'src/hooks/useSearchResultShown';
import { SearchType } from 'src/types/search/common';

import styles from './styles.less';

const getBorderStyle = (isNew: boolean, isSelected: boolean) => {
    if (isSelected) {
        return 'accent';
    }
    if (isNew) {
        return 'positive';
    }
    return 'neutral';
};

interface Props {
    isClickSelectionEnabled: boolean;
    isNew: boolean;
    isSelected: boolean;
    isHidden: boolean;
    onSelect?: (isSelected: boolean) => void;
    resumeId: string;
    resumeHash: string;
    resumeTopicId?: string;
    searchRid?: string;
}

const Wrapper: FC<Props & PropsWithChildren> = ({
    isClickSelectionEnabled,
    isNew,
    isSelected,
    isHidden,
    onSelect,
    resumeId,
    resumeHash,
    resumeTopicId,
    searchRid,
    children,
}) => {
    const vacancyId = useVacancyId();
    const { isGtM } = useBreakpoint();
    const isLoading = useLoadingState();

    const handleClick = useCallback(() => {
        onSelect?.(!isSelected);
        if (resumeTopicId && vacancyId) {
            responseSelectButtonClick({ topicId: resumeTopicId, vacancyId: `${vacancyId}` });
        }
    }, [isSelected, onSelect, resumeTopicId, vacancyId]);

    const searchResultShown = useSearchResultShown({ searchType: SearchType.Resume, itemId: resumeHash, searchRid });

    if (isHidden) {
        return null;
    }

    return (
        <ElementShownAnchor fn={searchResultShown} className={isLoading ? styles.loading : undefined}>
            <Card
                stretched
                padding={isGtM ? 24 : 16}
                borderRadius={36}
                borderWidth={isNew || isSelected ? 'selected' : 'default'}
                borderStyle={getBorderStyle(isNew, isSelected)}
                onClick={isClickSelectionEnabled ? handleClick : undefined}
                shadow="level-0"
                hoverEnabled
                increaseShadow
                data-qa={classnames('resume-serp__resume', {
                    'resume-serp__resume_new': isNew,
                    'resume-serp__resume_selected': isSelected,
                })}
                data-resume-id={resumeId}
            >
                {children}
            </Card>
        </ElementShownAnchor>
    );
};

export default Wrapper;
